import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import {
  ApexNonAxisChartSeries,
  ApexChart,
  ApexLegend,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { DashboardService } from 'src/app/services/dashboard.service';
import { OrganizationsService } from 'src/app/services/organization.service';
import { distinctUntilChanged, finalize } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { PlatformService } from 'src/app/services/platform.service';

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  colors: string[];
  legend: ApexLegend;
  plotOptions?: any;
  dataLabels?: any;
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatCardModule,
    MatSelectModule,
    NgApexchartsModule,
    FormsModule,
    NgxSpinnerModule,
  ],
})
export class DashboardComponent implements OnInit {
  public platform: string =
  this.platformService.getCurrentPlatform();
  public usersChart!: ChartOptions;
  public extensionsChart!: ChartOptions;
  public sdasChart!: ChartOptions;
  public devicesChart!: ChartOptions;

  private spinner = inject(NgxSpinnerService);

  constructor(
    private dashboardService: DashboardService,
    private organizationsService: OrganizationsService,
    private platformService: PlatformService,
  ) {}

 ngOnInit(): void {
  this.organizationsService.currentOrganizationChange$
    .pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id),
    )
    .subscribe((org) => {
      if (!org) return;

      this.loadDashboard(org.id);
    });
}

  private loadDashboard(orgId: string): void {
  this.resetCharts();

  this.spinner.show('spinner');

  this.dashboardService
    .getStats(orgId, this.platform)
    .pipe(
      finalize(() => {
        setTimeout(() => {
          this.spinner.hide('spinner');
        }, 300);
      }),
    )
    .subscribe({
      next: (stats) => {
        if (!stats) return;

        this.usersChart = this.buildDonutChart(
          [stats.usersActive ?? 0, stats.usersBlocked ?? 0],
          ['Actifs', 'Bloqués'],
          ['#4caf50', '#f44336'],
        );

        this.extensionsChart = this.buildDonutChart(
          [stats.groupsActive ?? 0, stats.groupsInactive ?? 0],
          ['Actifs', 'Inactifs'],
          ['#2196f3', '#9e9e9e'],
        );

        this.sdasChart = this.buildDonutChart(
          [stats.sdaAffected ?? 0, stats.sdaUnaffected ?? 0],
          ['Affectés', 'Non affectés'],
          ['#ff9800', '#9e9e9e'],
        );

        this.devicesChart = this.buildDonutChart(
          [
            stats.devicesOn ?? 0,
            stats.devicesHs ?? 0,
            stats.devicesDisconnected ?? 0,
          ],
          ['ON', 'HS', 'Non connectés'],
          ['#4caf50', '#f44336', '#9e9e9e'],
        );
      },
      error: (err) => {
        console.error('[Dashboard] Erreur getStats', err);
      },
    });
}

  onPlatformChange(): void {
  this.platformService.setPlatform(this.platform);

  const currentOrg =
    this.organizationsService.getCurrentOrganization();

  if (!currentOrg) return;

  this.loadDashboard(currentOrg.id);
}

  private buildDonutChart(
    series: number[],
    labels: string[],
    colors: string[],
  ): ChartOptions {
    return {
      series,
      chart: {
        type: 'donut',
        height: 250,
        foreColor: '#e0e0e0',
      },
      labels,
      colors,
      legend: {
        position: 'bottom',
        labels: {
          colors: '#e0e0e0',
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                color: '#e0e0e0',
                formatter: (w: any) => {
                  const total = w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0,
                  );
                  return `${total}`;
                },
              },
              value: {
                color: '#e0e0e0',
              },
            },
          },
        },
      },
      dataLabels: {
        style: {
          colors: ['#e0e0e0'],
        },
        formatter: (val: number, opts: any) => {
          const total = opts.w.globals.seriesTotals.reduce(
            (a: number, b: number) => a + b,
            0,
          );
          const current = opts.w.globals.series[opts.seriesIndex];
          return total > 0 ? `${((current / total) * 100).toFixed(1)}%` : '0%';
        },
      },
    };
  }

  private resetCharts(): void {
    this.usersChart = undefined as any;
    this.extensionsChart = undefined as any;
    this.sdasChart = undefined as any;
    this.devicesChart = undefined as any;
  }
}
